'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, Copy, MapPin } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { AppleMark, GoogleMapsMark } from './Marks';

/**
 * WHERE IT IS, AS A PLACE RATHER THAN A MENU
 * ==========================================
 *
 * Tapping the address used to drop a little menu: Google Maps, Apple Maps,
 * copy. Everything somebody needed and nothing they wanted — a list of three
 * apps is an answer to "how do I get there" from a page that never showed
 * them where "there" is. A parent deciding whether their fourteen-year-old
 * can get to Troy on a Wednesday is asking a question about geography, and we
 * were answering it with a dropdown.
 *
 * So it opens the same way the campus finder does: a sheet, a map across the
 * top, and the address under it with the same three things to do. The map is
 * the answer; the buttons are what you do about it.
 *
 * MP HAS ALREADY DONE THE GEOCODING. Staff save an address and MP places it —
 * 153 of the church's 158 locations carry a pin. So the coordinates ride down
 * with the event and this needs no geocoding call of its own, which would be
 * a second Google API to enable and a round trip on a page mostly read by
 * people standing still. For the handful MP never placed, there is no map and
 * the sheet is the address and the buttons, which is still better than the
 * menu was.
 *
 * INTERACTIVE, NOT A PICTURE. A static image would be lighter and is the
 * right shape for a header — but the Static Maps API is not enabled on the
 * church's key, while the Maps JS API is, because the campus finder has run
 * on it in production for months. This uses what is already authorised rather
 * than asking somebody to go into a Google Cloud console before the widget
 * works.
 */

/** The church's own key, the one the campus finder and profile widget use. */
export const DEFAULT_MAPS_KEY = 'AIzaSyBzf7AATUxLctt3O69OqPVbi3owcCWp17Y';

declare global {
  interface Window {
    google?: typeof google;
  }
}

let loader: Promise<void> | null = null;

/**
 * One script for the page, however many widgets want it.
 *
 * The campus finder may have loaded it already and a second tag would be a
 * second download and a console warning, so an existing tag is waited on
 * rather than duplicated.
 */
export function loadGoogleMaps(apiKey: string): Promise<void> {
  if (window.google?.maps?.Map) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise<void>((resolve, reject) => {
    const settle = () => {
      const check = window.setInterval(() => {
        if (window.google?.maps?.Map) {
          window.clearInterval(check);
          resolve();
        }
      }, 50);
      // Never wait forever: a blocked script would otherwise leave the sheet
      // showing a loading map for as long as it is open.
      window.setTimeout(() => {
        window.clearInterval(check);
        if (!window.google?.maps?.Map) {
          loader = null;
          reject(new Error('Google Maps did not load'));
        }
      }, 8000);
    };
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      settle();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = settle;
    script.onerror = () => {
      loader = null;
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });
  return loader;
}

/** A teardrop in the event's own accent, so the pin belongs to this page. */
function pinIcon(fill: string): google.maps.Icon {
  const svg = `<svg width="34" height="44" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="${fill}"/>
    <circle cx="16" cy="15" r="6" fill="#ffffff"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(34, 44),
    anchor: new google.maps.Point(17, 44),
  };
}

function Map({ lat, lng, accent }: { lat: number; lng: number; accent: string }) {
  const host = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let dead = false;
    loadGoogleMaps(DEFAULT_MAPS_KEY)
      .then(() => {
        if (dead || !host.current) return;
        const map = new google.maps.Map(host.current, {
          center: { lat, lng },
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
          clickableIcons: false,
        });
        new google.maps.Marker({ position: { lat, lng }, map, icon: pinIcon(accent) });
      })
      .catch(() => !dead && setFailed(true));
    return () => {
      dead = true;
    };
  }, [lat, lng, accent]);

  // A map that will not load is left out rather than left as a grey box: the
  // address and the buttons under it are the part that actually gets anybody
  // there.
  if (failed) return null;
  return <div ref={host} className="bg-mist h-48 w-full sm:h-56" aria-hidden="true" />;
}

export function WhereSheet({
  open,
  onClose,
  name,
  address,
  lat,
  lng,
  accent = '#299c8f',
}: {
  open: boolean;
  onClose: () => void;
  /** The venue, when MP names one: "Troy Campus". */
  name: string | null;
  /** One line, as it would be written on an envelope. */
  address: string;
  lat: number | null;
  lng: number | null;
  accent?: string;
}) {
  const [copied, setCopied] = useState(false);
  const dest = encodeURIComponent(address);
  const row =
    'text-navy flex items-center justify-center gap-2.5 rounded-xl border border-line bg-white px-3 py-3 text-sm font-bold transition hover:scale-[1.02] hover:shadow-md active:scale-95';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked: the address is on screen and can be selected */
    }
  };

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-lg"
      panelClassName="bg-ground text-ink md:rounded-3xl"
      noPanelPadding
      /* The map IS the header — the thing being answered, across the top,
         rather than a picture underneath a title repeating the address. */
      header={
        lat != null && lng != null ? (
          <Map lat={lat} lng={lng} accent={accent} />
        ) : (
          <div className="bg-navy px-5 py-6">
            <p className="text-lg font-bold text-white">{name ?? 'Where'}</p>
          </div>
        )
      }
    >
      {/*
       * "main", not "where".
       *
       * ResponsiveSheet's `defaultPage` is "main" and it finds the active
       * page by name — so a lone page called anything else matches nothing,
       * `activePage` is undefined, and the sheet renders its header over an
       * empty body. Which is exactly what happened: the map showed and the
       * address and the three buttons did not.
       */}
      <SheetPage name="main">
        <div className="space-y-4 p-4">
          <div className="flex items-start gap-3">
            <MapPin className="text-teal mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              {name && <p className="text-navy text-base font-bold">{name}</p>}
              <p className="text-slate text-sm">{address}</p>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${dest}`}
              target="_blank"
              rel="noopener"
              className={row}
            >
              <GoogleMapsMark className="h-5 w-5 shrink-0" />
              Google Maps
            </a>
            <a
              href={`https://maps.apple.com/?daddr=${dest}&dirflg=d`}
              target="_blank"
              rel="noopener"
              className={row}
            >
              <AppleMark className="h-5 w-5 shrink-0" />
              Apple Maps
            </a>
          </div>

          <button
            type="button"
            onClick={copy}
            className="text-slate hover:text-navy flex w-full items-center justify-center gap-1.5 py-1 text-xs font-semibold"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy address'}
          </button>
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
